import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getGSectionVO,
    saveGSectionPage,
} from '@/services/auditService/auditProgramService';
import {
    G0_PAGE,
    G0Page,
    G_PAGE_TITLES,
    G_SECTION_PAGES,
    GSectionPageCode,
} from '@/pages/components/audit-program/g-section/constants';
import G0ProgrammeView from '@/pages/components/audit-program/g-section/G0ProgrammeView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type GSectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: GSectionPageCode;
};

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function withHeaderDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        completedBy: page.completedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: page.ref || refCode,
    };
}

export default function GSectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'G0',
}: GSectionViewProps) {
    const [pageKey, setPageKey] = useState<GSectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('G');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getGSectionVO(companyId, annualId);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of G_SECTION_PAGES) {
                pages[code] = withHeaderDefaults(toStringRecord(data.pages?.[code]), data, code);
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load G Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: GSectionPageCode, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (!dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveGSectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const g0Content = (getContent('G0') ?? G0_PAGE) as G0Page;

    const tabs = useMemo(() => G_SECTION_PAGES.map((code) => ({
        key: code,
        label: <PageTabLabel code={code} title={G_PAGE_TITLES[code]}/>,
        icon: code,
        content: header ? (
            <G0ProgrammeView
                header={header}
                page={g0Content}
                refCode="G0"
                values={pageData.G0 ?? {}}
                onChange={(k, v) => handleFieldChange('G0', k, v)}
            />
        ) : null,
    })), [header, pageData, handleFieldChange, g0Content]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    G Section — Investments in group and associated undertakings
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    disabled={!dirty[pageKey] || saving}
                    onClick={() => void handleSave()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" variant="body2" sx={{px: 2}}>{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as GSectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
